import { makeAutoObservable, runInAction } from 'mobx';
import { WebCryptoUtils } from '../helpers/webCryptoUtils';

export class PatientStore {
    patientId: string | undefined = undefined;
    name: string | undefined = undefined;
    birthDate: string | undefined = undefined;
    therapeuticIndicationId: string | undefined = undefined;
    sexId: string | undefined = undefined;
    isAnonymizedMode: boolean | undefined = undefined;

    constructor() {
        makeAutoObservable(this);

        // Load encrypted data from localStorage asynchronously
        this.loadEncryptedData();

        // Listen for changes in other tabs
        window.addEventListener('storage', (event) => {
            runInAction(() => {
                // Handle storage events for encrypted data
                if (event.key === 'patientId' && event.newValue) 
                    this.decryptAndSetPatientId(event.newValue);
                
                if (event.key === 'patientName' && event.newValue) 
                    this.decryptAndSetPatientName(event.newValue);
                
                if (event.key === 'patientBirthDate' && event.newValue) 
                    this.decryptAndSetPatientBirthDate(event.newValue);
                
                if (event.key === 'therapeuticIndicationId' && event.newValue) 
                    this.decryptAndSetTherapeuticIndicationId(event.newValue);
                
                if (event.key === 'sexId' && event.newValue) 
                    this.decryptAndSetSexId(event.newValue);
                
                if (event.key === 'isAnonymizedMode' && event.newValue) 
                    this.decryptAndSetIsAnonymizedMode(event.newValue);
                
            });
        });
    }

    private async loadEncryptedData(): Promise<void> {
        try {
            const encryptedPatientId = localStorage.getItem('patientId');
            const encryptedName = localStorage.getItem('patientName');
            const encryptedBirthDate = localStorage.getItem('patientBirthDate');
            const encryptedTherapeuticIndicationId = localStorage.getItem('therapeuticIndicationId');
            const encryptedSexId = localStorage.getItem('sexId');
            const encryptedIsAnonymizedMode = localStorage.getItem('isAnonymizedMode');

            // Decrypt and set values
            if (encryptedPatientId) {
                const decrypted = await WebCryptoUtils.decrypt(encryptedPatientId);
                runInAction(() => {
                    this.patientId = decrypted;
                });
            }
            if (encryptedName) {
                const decrypted = await WebCryptoUtils.decrypt(encryptedName);
                runInAction(() => {
                    this.name = decrypted;
                });
            }
            if (encryptedBirthDate) {
                const decrypted = await WebCryptoUtils.decrypt(encryptedBirthDate);
                runInAction(() => {
                    this.birthDate = decrypted;
                });
            }
            if (encryptedTherapeuticIndicationId) {
                const decrypted = await WebCryptoUtils.decrypt(encryptedTherapeuticIndicationId);
                runInAction(() => {
                    this.therapeuticIndicationId = decrypted;
                });
            }
            if (encryptedSexId) {
                const decrypted = await WebCryptoUtils.decrypt(encryptedSexId);
                runInAction(() => {
                    this.sexId = decrypted;
                });
            }
            if (encryptedIsAnonymizedMode) {
                const decryptedValue = await WebCryptoUtils.decrypt(encryptedIsAnonymizedMode);
                const decrypted = decryptedValue === 'true';
                runInAction(() => {
                    this.isAnonymizedMode = decrypted;
                });
            }
        } catch (error) {
            console.error('Failed to load encrypted patient data:', error);
            // Clear potentially corrupted data
            this.clearPatientId();
        }
    }

    private async decryptAndSetPatientId(encryptedValue: string): Promise<void> {
        try {
            const decrypted = await WebCryptoUtils.decrypt(encryptedValue);
            runInAction(() => {
                this.patientId = decrypted;
            });
        } catch (error) {
            console.error('Failed to decrypt patient ID:', error);
        }
    }

    private async decryptAndSetPatientName(encryptedValue: string): Promise<void> {
        try {
            const decrypted = await WebCryptoUtils.decrypt(encryptedValue);
            runInAction(() => {
                this.name = decrypted;
            });
        } catch (error) {
            console.error('Failed to decrypt patient name:', error);
        }
    }

    private async decryptAndSetPatientBirthDate(encryptedValue: string): Promise<void> {
        try {
            const decrypted = await WebCryptoUtils.decrypt(encryptedValue);
            runInAction(() => {
                this.birthDate = decrypted;
            });
        } catch (error) {
            console.error('Failed to decrypt patient birth date:', error);
        }
    }

    private async decryptAndSetTherapeuticIndicationId(encryptedValue: string): Promise<void> {
        try {
            const decrypted = await WebCryptoUtils.decrypt(encryptedValue);
            runInAction(() => {
                this.therapeuticIndicationId = decrypted;
            });
        } catch (error) {
            console.error('Failed to decrypt therapeutic indication ID:', error);
        }
    }

    private async decryptAndSetSexId(encryptedValue: string): Promise<void> {
        try {
            const decrypted = await WebCryptoUtils.decrypt(encryptedValue);
            runInAction(() => {
                this.sexId = decrypted;
            });
        } catch (error) {
            console.error('Failed to decrypt sex ID:', error);
        }
    }

    private async decryptAndSetIsAnonymizedMode(encryptedValue: string): Promise<void> {
        try {
            const decryptedValue = await WebCryptoUtils.decrypt(encryptedValue);
            const decrypted = decryptedValue === 'true';
            runInAction(() => {
                this.isAnonymizedMode = decrypted;
            });
        } catch (error) {
            console.error('Failed to decrypt is anonymized mode:', error);
        }
    }

    async setPatientId(id: string): Promise<void> {
        try {
            const encrypted = await WebCryptoUtils.encrypt(id);
            runInAction(() => {
                this.patientId = id;
            });
            localStorage.setItem('patientId', encrypted);
        } catch (error) {
            console.error('Failed to encrypt and store patient ID:', error);
            throw error;
        }
    }

    async setPatientName(name: string): Promise<void> {
        try {
            const encrypted = await WebCryptoUtils.encrypt(name);
            runInAction(() => {
                this.name = name;
            });
            localStorage.setItem('patientName', encrypted);
        } catch (error) {
            console.error('Failed to encrypt and store patient name:', error);
            throw error;
        }
    }

    async setPatientBirthDate(birthDate: string): Promise<void> {
        try {
            const encrypted = await WebCryptoUtils.encrypt(birthDate);
            runInAction(() => {
                this.birthDate = birthDate;
            });
            localStorage.setItem('patientBirthDate', encrypted);
        } catch (error) {
            console.error('Failed to encrypt and store patient birth date:', error);
            throw error;
        }
    }

    async setTherapeuticIndicationId(therapeuticIndicationId: string): Promise<void> {
        try {
            const encrypted = await WebCryptoUtils.encrypt(therapeuticIndicationId);
            runInAction(() => {
                this.therapeuticIndicationId = therapeuticIndicationId;
            });
            localStorage.setItem('therapeuticIndicationId', encrypted);
        } catch (error) {
            console.error('Failed to encrypt and store therapeutic indication ID:', error);
            throw error;
        }
    }

    async setSexId(sexId: string): Promise<void> {
        try {
            const encrypted = await WebCryptoUtils.encrypt(sexId);
            runInAction(() => {
                this.sexId = sexId;
            });
            localStorage.setItem('sexId', encrypted);
        } catch (error) {
            console.error('Failed to encrypt and store sex ID:', error);
            throw error;
        }
    }

    async setIsAnonymizedMode(isAnonymizedMode: boolean): Promise<void> {
        try {
            const encrypted = await WebCryptoUtils.encrypt(isAnonymizedMode.toString());
            runInAction(() => {
                this.isAnonymizedMode = isAnonymizedMode;
            });
            localStorage.setItem('isAnonymizedMode', encrypted);
        } catch (error) {
            console.error('Failed to encrypt and store is anonymized mode:', error);
            throw error;
        }
    }
    
    clearPatientId(): void {
        runInAction(() => {
            this.patientId = undefined;
            this.name = undefined;
            this.birthDate = undefined;
            this.therapeuticIndicationId = undefined;
            this.sexId = undefined;
            this.isAnonymizedMode = undefined;
        });
        localStorage.removeItem('patientId');
        localStorage.removeItem('patientName');
        localStorage.removeItem('patientBirthDate');
        localStorage.removeItem('therapeuticIndicationId');
        localStorage.removeItem('sexId');
        localStorage.removeItem('isAnonymizedMode');

        // Clear the encryption key cache as well
        WebCryptoUtils.clearKey();
    }
}
